import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { AddMenuItems } from './AddMenuItems';
import { EventPill } from './EventPill';

/**
 * MonthGrid — 6-row month calendar with drag & drop.
 * Renders day cells with up to 3 event pills each; extra events are
 * rolled up into a "+N more" indicator.
 */
export const MonthGrid = ({
  currentDate,
  getEventsForDate,
  propertyColorMap,
  dragOverDate,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onEventClick,
  onCellClick,
  onDayPickType,
  showPropertyName,
}) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="calendar-grid bg-slate-50 border-b border-slate-200">
        {weekDays.map((d) => (
          <div
            key={d}
            className="p-3 text-center text-sm font-medium text-slate-600 uppercase tracking-wider"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="calendar-grid">
        {days.map((d) => {
          const dayEvents = getEventsForDate(d);
          const isToday = isSameDay(d, new Date());
          const isCurrentMonth = isSameMonth(d, currentDate);
          const dateStr = format(d, 'yyyy-MM-dd');
          const isDragOver = dragOverDate === dateStr;

          return (
            <DropdownMenu key={dateStr}>
              <DropdownMenuTrigger asChild disabled={!isCurrentMonth}>
                <div
                  className={`calendar-cell group ${!isCurrentMonth ? 'outside-month' : ''} ${isToday ? 'today' : ''} ${isDragOver ? 'ring-2 ring-blue-400 bg-blue-50' : ''} ${isCurrentMonth ? 'cursor-pointer' : ''}`}
                  onDragOver={(e) => onDragOver(e, d)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, d)}
                  onClick={() => isCurrentMonth && onCellClick(d)}
                  data-testid={`calendar-cell-${dateStr}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`text-sm font-medium ${isToday ? 'bg-blue-600 text-white px-2 py-0.5 rounded-full' : ''} ${!isCurrentMonth ? 'text-slate-400' : 'text-slate-700'}`}
                    >
                      {format(d, 'd')}
                    </span>
                    {isCurrentMonth && (
                      <Plus className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>

                  <div className="space-y-1 overflow-y-auto max-h-[80px]">
                    {dayEvents.slice(0, 3).map((event) => (
                      <EventPill
                        key={`${event.type}-${event.id}`}
                        event={event}
                        propertyColorMap={propertyColorMap}
                        onEventClick={onEventClick}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        showPropertyName={showPropertyName}
                        variant="month"
                        testIdPrefix="event"
                      />
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-slate-500 pl-2">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              </DropdownMenuTrigger>
              {isCurrentMonth && (
                <DropdownMenuContent align="start">
                  <AddMenuItems label onPick={(type) => onDayPickType(d, type)} />
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          );
        })}
      </div>
    </div>
  );
};

export default MonthGrid;
